import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button } from "../../Components/UI/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, } from "../../Components/UI/dialog";
import { Check, X, ArrowLeft, Plus, Pencil } from 'lucide-react';
import { updateConnFieldPrefsAPI, updateAllFieldPrefsAPI } from "./ConnectionAPI";
import { selectConnFieldPrefs, selectConnFieldPref, selectConnFieldPrefsLoading, getAgencyDetails, isSmartSellerMode, getThreadTypesArr, selectThreadFieldPref, checkIfInqOnlyMode } from "../../Store/Reducers";
import { updateThreadFieldPrefsAPI } from "../Threads/ThreadsAPI";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../Components/UI/tooltip';
import { toast } from "sonner";
import { Checkbox } from "../../Components/UI/checkbox";
import { Label } from "../../Components/UI/label";
import { getThreadNameFromTypeID } from "../../Utils/Utilities";
import { CustomDropdown } from '../../Components/Common/DropDown';
import ExtraFieldEntryDialog from '../../Modules/Settings/ExtraFieldEntryDialog'; // Adjust path if needed
import useIsMobile from "../../Hooks/useIsMobile";
import { AlertBox } from "../../Components/Common/AlertBox";

const ConnectionFieldsDialog = ({ open, onOpenChange, onSave, connID, forcedFields = [], isInquiryChecked = false, isEditingContact, hideTitles = false, calledFrom = '', threadTypeID = null, connType = '' }) => {
    const isEditMode = !!connID;

    // --- EXTRA FIELD DIALOG STATE ---
    const [extraDialogOpen, setExtraDialogOpen] = useState(false);
    const [extraDialogConfig, setExtraDialogConfig] = useState({
        mode: 'add', // 'add' | 'edit'
        type: 'connection', // 'connection' | 'thread'
        initialValue: '',
        fieldID: null,
        threadTypeID: null,
        threadTypeName: ''
    });

    const openExtraFieldDialog = (mode, type, data = {}) => {
        setExtraDialogConfig({
            mode,
            type,
            initialValue: data.name || '',
            fieldID: data.id,
            threadTypeID: data.threadTypeID || null,
            threadTypeName: data.threadTypeName || ''
        });
        setExtraDialogOpen(true);
    };
    const agencyData = useSelector(getAgencyDetails);
    const threadTypes = useSelector(getThreadTypesArr);
    const isInqOnlyMode = useSelector(checkIfInqOnlyMode);

    // --- MASTER DATA FOR EXTRA FIELDS ---
    const connExtraFields = useSelector(state => state.masterData.connExtraFieldsArr || []);
    const thrExtraFields = useSelector(state => state.masterData.thrExtraFields || []);

    // State for Thread Type Switching
    const [currentThreadType, setCurrentThreadType] = useState(threadTypeID || (isInqOnlyMode ? 1 : null));

    useEffect(() => {
        if (open) {
            setCurrentThreadType(threadTypeID || (isInqOnlyMode ? 1 : null));
        }
    }, [open, threadTypeID, isInqOnlyMode]);

    // --- CONNECTION PREFS SELECTORS ---
    const connFieldPrefs = useSelector(selectConnFieldPrefs);
    const loading = useSelector(selectConnFieldPrefsLoading);
    const isMobile = useIsMobile()

    const connPrefsEnabled = {
        city: useSelector((state) => selectConnFieldPref(state, "City")),
        state: useSelector((state) => selectConnFieldPref(state, "State")),
        country: useSelector((state) => selectConnFieldPref(state, "Country")),
        gstin: useSelector((state) => selectConnFieldPref(state, "TaxID")),
        potential: useSelector((state) => selectConnFieldPref(state, "Potential")),
        source: useSelector((state) => selectConnFieldPref(state, "Source")),
        since: useSelector((state) => selectConnFieldPref(state, "Since")),
        segment: useSelector((state) => selectConnFieldPref(state, "Segement")),
        poc: useSelector((state) => selectConnFieldPref(state, "POC")),
        nextTask: useSelector((state) => selectConnFieldPref(state, "NextTask")),
        notes: useSelector((state) => selectConnFieldPref(state, "Notes")),
        tags: useSelector((state) => selectConnFieldPref(state, "Tags")),
        email: useSelector((state) => selectConnFieldPref(state, "Email")),
        region: useSelector((state) => selectConnFieldPref(state, "Region"))
    };

    // --- THREAD PREFS SELECTORS ---
    const threadPrefsEnabled = {
        contactPerson: useSelector(state => selectThreadFieldPref(state, 'POC', currentThreadType)),
        model: useSelector(state => selectThreadFieldPref(state, 'Item', currentThreadType)),
        quantity: useSelector(state => selectThreadFieldPref(state, 'Qty', currentThreadType)),
        assignedTo: useSelector(state => selectThreadFieldPref(state, 'AsgnTo', currentThreadType)),
        nextAction: useSelector(state => selectThreadFieldPref(state, 'NextTsk', currentThreadType)),
        sinceDT: useSelector(state => selectThreadFieldPref(state, 'Since', currentThreadType)),
        source: useSelector(state => selectThreadFieldPref(state, 'Source', currentThreadType)),
        reference: useSelector(state => selectThreadFieldPref(state, 'Ref', currentThreadType))
    };

    // --- LOCAL STATE ---
    // Using two separate arrays to manage selections independently
    const [selectedConnFields, setSelectedConnFields] = useState([]);
    const [selectedThreadFields, setSelectedThreadFields] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [showExitAlert, setShowExitAlert] = useState(false);

    // Init Connection Fields
    useEffect(() => {
        if (open) {
            const initialConn = ["name", "businessName", "mobile"];
            if (connPrefsEnabled.city) initialConn.push("city");
            if (connPrefsEnabled.state) initialConn.push("state");
            if (connPrefsEnabled.country) initialConn.push("country");
            if (connPrefsEnabled.gstin) initialConn.push("gstin");
            if (connPrefsEnabled.potential) initialConn.push("businessPotential");
            if (connPrefsEnabled.source) initialConn.push("source");
            if (connPrefsEnabled.since) initialConn.push("since");
            if (connPrefsEnabled.segment) initialConn.push("segment");
            if (connPrefsEnabled.poc) initialConn.push("assignedTo");
            if (connPrefsEnabled.nextTask && !isEditMode) initialConn.push("nextTask");
            if (connPrefsEnabled.notes) initialConn.push("notes");
            if (connPrefsEnabled.tags) initialConn.push("tags");
            if (connPrefsEnabled.email) initialConn.push("email");
            if (connPrefsEnabled.region) initialConn.push("region");

            // Ensure forced fields are selected
            if (forcedFields?.length > 0) {
                forcedFields.forEach(f => {
                    if (!initialConn.includes(f)) initialConn.push(f);
                });
            }

            setSelectedConnFields(initialConn);
        }
    }, [open, connFieldPrefs, isEditMode, forcedFields]); // Reload when open or mode changes

    // Reset hasChanges only when dialog first opens
    useEffect(() => {
        if (open) {
            setHasChanges(false);
        }
    }, [open]);

    // Init Thread Fields (Reload when Thread Type changes)
    useEffect(() => {
        if (open) {
            const currentTypeObj = threadTypes.find(tt => tt.TID == currentThreadType);
            const isInquiryType = currentTypeObj?.SnSng === "Inquiry";

            const initialThread = ['product', 'targetDate', 'businessValue', 'notes', 'model', 'quantity', 'contactPerson'];
            if (currentThreadType == 2 && !isSmartSellerMode()) initialThread.push('assignedTo');
            // Mandatory logic handled in toggle, here just pre-fill enabled prefs
            if (threadPrefsEnabled.contactPerson) initialThread.push('contactPerson');
            if (threadPrefsEnabled.model) initialThread.push('model');
            if (threadPrefsEnabled.quantity) initialThread.push('quantity');
            if (currentThreadType != 2 && threadPrefsEnabled.assignedTo) initialThread.push('assignedTo');
            if (threadPrefsEnabled.nextAction && !isEditMode) initialThread.push('nextAction');
            if (threadPrefsEnabled.sinceDT) initialThread.push('sinceDT');
            if (isInquiryType && threadPrefsEnabled.reference) initialThread.push('reference');

            setSelectedThreadFields(initialThread);

            // Reset hasChanges when thread type changes (not on every render)
            setHasChanges(false);
        }
    }, [open, currentThreadType, isEditMode]);

    // --- HELPERS ---
    const handleConnFieldToggle = (fieldId) => {
        if (loading) return;
        const mandatory = ["name", "businessName", "mobile"];
        if (mandatory.includes(fieldId)) return;

        const isCurrentlySelected = selectedConnFields.includes(fieldId);

        const connToThreadMap = {
            'since': 'sinceDT'
        };

        // Track that changes have been made
        setHasChanges(true);

        // Logic for Next Task / Assigned To dependency
        if (fieldId === 'nextTask' && !isEditMode) {
            if (!isCurrentlySelected) {
                setSelectedConnFields(prev => [...prev, 'nextTask', 'assignedTo']);
            } else {
                setSelectedConnFields(prev => prev.filter(f => f !== 'nextTask'));
            }
        } else if (fieldId === 'assignedTo') {
            const hasNextTask = selectedConnFields.includes('nextTask');
            if (hasNextTask && isCurrentlySelected && !isEditMode) return;
            setSelectedConnFields(prev => isCurrentlySelected ? prev.filter(f => f !== fieldId) : [...prev, fieldId]);
        } else if (connToThreadMap[fieldId]) {
            const threadField = connToThreadMap[fieldId];
            if (!isCurrentlySelected) {
                setSelectedConnFields(prev => [...prev, fieldId]);
                setSelectedThreadFields(prev => prev.includes(threadField) ? prev : [...prev, threadField]);
            } else {
                setSelectedConnFields(prev => prev.filter(f => f !== fieldId));
                setSelectedThreadFields(prev => prev.filter(f => f !== threadField));
            }
        } else {
            setSelectedConnFields(prev => isCurrentlySelected ? prev.filter(f => f !== fieldId) : [...prev, fieldId]);
        }
    };

    const handleThreadFieldToggle = (fieldId) => {
        if (loading) return;
        const currentTypeObj = threadTypes.find(tt => tt.TID == currentThreadType);
        const isInquiryType = currentTypeObj?.SnSng === "Inquiry";

        let bothChecked = ["source", "sinceDT", "assignedTo"]

        let mandatory = ['product', 'targetDate', 'businessValue', 'notes', 'model', 'quantity', 'contactPerson'];
        if (currentThreadType == 2) {
            mandatory.push('assignedTo');
        }
        if (mandatory.includes(fieldId)) return;

        const isCurrentlySelected = selectedThreadFields.includes(fieldId);

        const threadToConnMap = {
            'sinceDT': 'since'
        };

        // Track that changes have been made
        setHasChanges(true);

        // Logic for Next Action / Assigned To dependency
        if (fieldId === 'nextAction' && !isEditMode) {
            if (!isCurrentlySelected) {
                setSelectedThreadFields(prev => [...prev, 'nextAction', 'assignedTo']);
            } else {
                setSelectedThreadFields(prev => prev.filter(f => f !== 'nextAction'));
            }
        } else if (fieldId === 'assignedTo') {
            const hasNextAction = selectedThreadFields.includes('nextAction');
            if (hasNextAction && isCurrentlySelected && !isEditMode) return;
            setSelectedThreadFields(prev => isCurrentlySelected ? prev.filter(f => f !== fieldId) : [...prev, fieldId]);
        } else if (threadToConnMap[fieldId]) {
            const connField = threadToConnMap[fieldId];
            if (!isCurrentlySelected) {
                setSelectedThreadFields(prev => [...prev, fieldId]);
                setSelectedConnFields(prev => prev.includes(connField) ? prev : [...prev, connField]);
            } else {
                setSelectedThreadFields(prev => prev.filter(f => f !== fieldId));
                setSelectedConnFields(prev => prev.filter(f => f !== connField));
            }
        } else {
            setSelectedThreadFields(prev => isCurrentlySelected ? prev.filter(f => f !== fieldId) : [...prev, fieldId]);
        }
    };

    const handleSave = async () => {
        if (!hasChanges) {
            onOpenChange(false);
            return;
        }

        try {
            // 1. Prepare Connection Prefs
            const connPreferences = {
                City: selectedConnFields.includes("city"),
                State: selectedConnFields.includes("state"),
                Country: selectedConnFields.includes("country"),
                TaxID: selectedConnFields.includes("gstin"),
                Potential: selectedConnFields.includes("businessPotential"),
                Source: selectedConnFields.includes("source"),
                Since: selectedConnFields.includes("since"),
                Segement: selectedConnFields.includes("segment"),
                POC: selectedConnFields.includes("assignedTo"),
                NextTask: selectedConnFields.includes("nextTask"),
                Notes: selectedConnFields.includes("notes"),
                Tags: selectedConnFields.includes("tags"),
                Email: selectedConnFields.includes("email"),
                Region: selectedConnFields.includes("region"),
            };

            const threadPreferences = {
                POC: selectedThreadFields.includes('contactPerson'),
                Item: selectedThreadFields.includes('model'),
                Qty: selectedThreadFields.includes('quantity'),
                AsgnTo: selectedThreadFields.includes('assignedTo'),
                NextTsk: selectedThreadFields.includes('nextAction'),
                Since: selectedThreadFields.includes('sinceDT'),
                Ref: selectedThreadFields.includes('reference')
            };

            // 3. Save Both in one attempt
            await updateAllFieldPrefsAPI(connPreferences, threadPreferences, currentThreadType);

            toast.success('Preferences saved.');

            if (onSave) onSave(selectedConnFields);
            setHasChanges(false);
            onOpenChange(false);
        } catch (error) {
            console.error("Failed to save preferences:", error);
            toast.error("Failed to save preferences.");
        }
    };
    // Handle close - reset to original state if there are unsaved changes
    const handleClose = () => {
        if (hasChanges) {
            setShowExitAlert(true);
            return;
        }
        setHasChanges(false);
        onOpenChange(false);
    };

    const renderActionButtons = () => {
        const buttonContent = (
            <>
                <Check className="h-4 w-4" />
                {loading ? 'Saving...' : 'Save Fields'}
            </>
        );

        const cancelButton = (className) => (
            <Button
                onClick={handleClose}
                variant="outline"
                disabled={loading}
                className={className}
            >
                <X className="w-3 h-3" />
                {hasChanges ? 'Cancel' : 'Close'}
            </Button>
        );

        const saveButton = (className) => (
            <Button
                onClick={handleSave}
                disabled={loading || !hasChanges}
                className={className}
            >
                {buttonContent}
            </Button>
        );

        return (
            <div>
                <div className="flex gap-3 sm:hidden w-full mb-3">
                    {saveButton("flex-1 sahi-primary-button")}
                    {/* {cancelButton("flex-1 sahi-secondary-button")} */}
                </div>
                <div className="hidden sm:flex justify-start gap-3 w-full pb-4">
                    {saveButton("sahi-primary-button ")}
                    {cancelButton("sahi-secondary-button")}
                </div>
            </div>
        );
    };

    return (
        <TooltipProvider>
            <Dialog open={open} onOpenChange={(val) => !val ? handleClose() : onOpenChange(val)} preventOutsideClick>
                <DialogContent>
                    <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                        <div className="sm:hidden flex items-center justify-between w-full h-8">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <button type="button" tabIndex={-1} onClick={handleClose} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                    <ArrowLeft className="h-[24px] w-[24px]" />
                                    <span className="sr-only">Back</span>
                                </button>
                                <DialogTitle className="text-lg font-medium">
                                    <div className="truncate">
                                        Manage Fields
                                        {loading && " (Saving…)"}
                                        {hasChanges && !loading && " *"}
                                    </div>
                                </DialogTitle>
                            </div>
                        </div>

                        <div className={`hidden sm:flex items-center justify-between w-full`}>
                            <div className="flex items-center gap-3">
                                <div>
                                    <DialogTitle className="hidden sm:block text-lg md:text-xl">
                                        Manage Fields
                                        {loading && " (Saving…)"}
                                        {hasChanges && !loading && " *"}
                                    </DialogTitle>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <DialogClose
                                    onClick={handleClose}
                                    className={`h-9 w-9 p-0 hover:bg-bg-grey flex items-center justify-center`}
                                >
                                    <X className={`h-4 w-4 text-color`} />
                                </DialogClose>
                            </div>
                        </div>


                    </DialogHeader>

                    <div className="px-4  flex-1 overflow-y-auto sm:max-h-[75vh] space-y-4">

                        {(() => {
                            const isContactTitleVisible = !isEditingContact && isInquiryChecked && !hideTitles && calledFrom !== "THREAD_ENTRY";

                            return (
                                <>
                                    {isContactTitleVisible && <h3 className="text-base font-semibold text-gray-800  px-1 mb-3">Contact</h3>}

                                    {/* Only show connection section based on calledFrom */}
                                    {/* Only show connection section based on calledFrom */}
                                    {((calledFrom === "CONNECTION_ENTRY" && !isMobile) || (calledFrom === "CONNECTION_ENTRY" && isMobile) || (isEditingContact && calledFrom !== "THREAD_ENTRY")) && (
                                        <div className="border border-gray-200 sahi-radius px-4 py-4 bg-white shadow-sm">
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-4">
                                                {[
                                                    { id: "name", label: "Name", mandatory: true },
                                                    { id: "businessName", label: "Business Name", mandatory: true },
                                                    { id: "mobile", label: "Mobile", mandatory: true },
                                                    { id: "email", label: "Email" },
                                                    { id: "city", label: "City" },
                                                    { id: "state", label: "State" },
                                                    { id: "country", label: "Country" },
                                                    { id: "gstin", label: agencyData.Country == "India" ? "GSTIN" : "Tax ID" },
                                                    { id: "source", label: "Source" },
                                                    { id: "since", label: "Since" },
                                                    { id: "segment", label: "Category" },
                                                    { id: "businessPotential", label: "Business Potential" },
                                                    ...(isSmartSellerMode() ? [] : [{ id: "assignedTo", label: "Assigned to" }]),
                                                    // ...(isEditMode ? [] : [{ id: "nextTask", label: "Next Task" }]),
                                                    { id: "notes", label: "Notes" },
                                                    { id: "tags", label: "Tags" },
                                                    { id: "region", label: "Region" },
                                                ].filter(field => {
                                                    const isSupplierOrOther = connType && (
                                                        connType.split(',').map(t => t.trim().toLowerCase()).includes('supplier') ||
                                                        connType.split(',').map(t => t.trim().toLowerCase()).includes('other') ||
                                                        connType.toLowerCase() === 's' ||
                                                        connType.toLowerCase() === 'o'
                                                    );
                                                    if (isSupplierOrOther && (field.id === 'segment' || field.id === 'businessPotential')) {
                                                        return false;
                                                    }
                                                    return true;
                                                }).map(field => {
                                                    const isForced = forcedFields.includes(field.id);
                                                    const isSelected = selectedConnFields.includes(field.id) || isForced;
                                                    const isDisabled = field.mandatory || isForced || (field.id === 'assignedTo' && selectedConnFields.includes('nextTask') && !isEditMode);

                                                    return (
                                                        <div key={field.id} className={`flex items-center space-x-2 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                            <Checkbox
                                                                id={`conn-${field.id}`}
                                                                checked={isSelected}
                                                                onCheckedChange={() => !field.mandatory && !isForced && handleConnFieldToggle(field.id)}
                                                                disabled={isDisabled}
                                                                className="border-gray-300"
                                                            />
                                                            <Label htmlFor={`conn-${field.id}`} className="cursor-pointer font-normal">{field.label}</Label>
                                                        </div>
                                                    );
                                                })}


                                            </div>

                                            {/* Extra Fields List (Editable - Tag Style) */}
                                            <div className={`mt-3 ${isMobile ? 'border-t pt-2' : ''}`}>
                                                <h4 className="text-xs font-medium text-gray-500 mb-2">Custom Fields</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {connExtraFields.map((field, index) => (
                                                        <span
                                                            key={field.FID || index}
                                                            className="inline-flex items-center sahi-radius border px-2.5 py-0.5 text-xs font-semibold border-none transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-gray-100 hover:bg-gray-200 h-7 sahi-badge"
                                                        >
                                                            {field.F}
                                                            <Pencil
                                                                className="ml-2 h-3 w-3 cursor-pointer text-gray-500 hover:text-sahi-blue"
                                                                onClick={() => openExtraFieldDialog('edit', 'connection', {
                                                                    name: field.F,
                                                                    id: field.FID
                                                                })}
                                                            />
                                                        </span>
                                                    ))}
                                                    <button
                                                        onClick={() => openExtraFieldDialog('add', 'connection')}
                                                        className={`inline-flex items-center sahi-radius px-2.5 py-0.5 text-xs font-semibold transition ease-in-out delay-150 hover:-translate-y-1 duration-200 h-7 sahi-badge bg-white hover:bg-gray-50`}
                                                    >
                                                        {connExtraFields.length === 0 ? (
                                                            <>
                                                                <Plus className="h-3 w-3 " /> Add Custom Field
                                                            </>
                                                        ) : (
                                                            <Plus className="h-3 w-3" />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {/* --- THREADS SECTION --- */}
                                    {(!isEditingContact) && (isInquiryChecked) && (<>
                                        <div className="flex justify-between items-center mb-1 ">
                                            {(!hideTitles) && <h3 className="text-base font-semibold text-gray-800">
                                                {isInqOnlyMode ? "Inquiry" : "Thread"}
                                            </h3>}
                                            {!isInqOnlyMode && (
                                                <div className="w-40 text-right">
                                                    <CustomDropdown
                                                        options={threadTypes.map(t => ({ label: t.Typ, value: t.TID }))}
                                                        value={currentThreadType}
                                                        onChange={(val) => setCurrentThreadType(val)}
                                                        placeholder="Select Type"
                                                        label=""
                                                        className="h-8 text-xs"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {currentThreadType && <div className={`border border-gray-200 sahi-radius px-4 py-4 ${!((calledFrom === "CONNECTION_ENTRY" && !isMobile) || (calledFrom === "CONNECTION_ENTRY" && isMobile) || (isEditingContact && calledFrom !== "THREAD_ENTRY")) && '!mt-0'} bg-white shadow-sm`}>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-4">
                                                {[
                                                    { id: "product", label: "Offering", mandatory: true },
                                                    { id: "targetDate", label: "Target Date", mandatory: true },
                                                    { id: "businessValue", label: "Business Value", mandatory: true },
                                                    { id: "notes", label: "Notes", mandatory: true },
                                                    { id: "contactPerson", label: "Contact Person", mandatory: true },
                                                    { id: "model", label: "Item / Model", mandatory: true },
                                                    { id: "quantity", label: "Quantity", mandatory: true },
                                                    { id: "sinceDT", label: "Received on" },
                                                    // ...(isContactTitleVisible ? [] : [{ id: "source", label: "Source" }]),
                                                    { id: "reference", label: "Reference", mandatory: threadTypes.find(t => t.TID == currentThreadType)?.SnSng !== "Inquiry" },
                                                    ...(isSmartSellerMode() ? [] : [{ id: "assignedTo", label: currentThreadType == 2 ? "Credit to" : "Assigned to", mandatory: currentThreadType == 2 }]),
                                                    // ...(isEditMode ? [] : [{ id: "nextAction", label: "Next Task" }]),
                                                ].map(field => {
                                                    const isSelected = selectedThreadFields.includes(field.id);
                                                    const isDisabled = field.mandatory || (field.id === 'assignedTo' && selectedThreadFields.includes('nextAction') && !isEditMode);

                                                    return (
                                                        <div key={field.id} className={`flex items-center space-x-2 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                            <Checkbox
                                                                id={`thr-${field.id}`}
                                                                checked={isSelected}
                                                                onCheckedChange={() => !field.mandatory && handleThreadFieldToggle(field.id)}
                                                                disabled={isDisabled}
                                                                className="border-gray-300"
                                                            />
                                                            <Label htmlFor={`thr-${field.id}`} className="cursor-pointer font-normal">{field.label}</Label>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Extra Fields List (Threads - Editable - Tag Style) */}
                                            {(() => {
                                                const currentThrExtra = thrExtraFields.find(t => t.TID === currentThreadType);
                                                const fields = currentThrExtra ? JSON.parse(currentThrExtra.Fields).filter(f => !f.IsD) : [];

                                                return (
                                                    <div className={`mt-3 ${isMobile ? 'border-t pt-2' : ''}`}>
                                                        <h4 className="text-xs font-medium text-gray-500 mb-2">Custom Fields</h4>
                                                        <div className="flex flex-wrap gap-2">
                                                            {fields.map((field) => (
                                                                <span
                                                                    key={field.FID}
                                                                    className="inline-flex items-center sahi-radius border px-2.5 py-0.5 text-xs font-semibold border-none transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-gray-100 hover:bg-gray-200 h-7 sahi-badge"
                                                                >
                                                                    {field.F}
                                                                    <Pencil
                                                                        className="ml-2 h-3 w-3 cursor-pointer text-gray-500 hover:text-sahi-blue"
                                                                        onClick={() => openExtraFieldDialog('edit', 'thread', {
                                                                            name: field.F,
                                                                            id: field.FID,
                                                                            threadTypeID: currentThreadType,
                                                                            threadTypeName: getThreadNameFromTypeID(currentThreadType, threadTypes)
                                                                        })}
                                                                    />
                                                                </span>
                                                            ))}
                                                            <button
                                                                onClick={() => openExtraFieldDialog('add', 'thread', {
                                                                    threadTypeID: currentThreadType,
                                                                    threadTypeName: getThreadNameFromTypeID(currentThreadType, threadTypes)
                                                                })}
                                                                className={`inline-flex items-center sahi-radius px-2.5 py-0.5 text-xs font-semibold transition ease-in-out delay-150 hover:-translate-y-1 duration-200 h-7 sahi-badge bg-white hover:bg-gray-50 `}
                                                            >
                                                                {fields.length === 0 ? (
                                                                    <>
                                                                        <Plus className="h-3 w-3 mr-1" /> Add Custom Field
                                                                    </>
                                                                ) : (
                                                                    <Plus className="h-3 w-3" />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>}
                                    </>)}
                                </>
                            )
                        })()}

                        {/* ✅ Render action buttons at the bottom */}
                        {renderActionButtons()}
                    </div >
                </DialogContent>
            </Dialog>
            {/* New Lightweight Dialog for Extra Fields */}
            <ExtraFieldEntryDialog
                open={extraDialogOpen}
                onOpenChange={setExtraDialogOpen}
                mode={extraDialogConfig.mode}
                type={extraDialogConfig.type}
                initialValue={extraDialogConfig.initialValue}
                fieldID={extraDialogConfig.fieldID}
                threadTypeID={extraDialogConfig.threadTypeID}
                threadTypeName={extraDialogConfig.threadTypeName}
            />

            <AlertBox
                open={showExitAlert}
                onOpenChange={setShowExitAlert}
                title="Please Confirm"
                content={
                    <div>
                        <p className="mb-1">You have unsaved changes.</p>
                        <p>Are you sure you want to go back?</p>
                    </div>
                }
                actions={[
                    {
                        label: "No",
                        variant: "cancel",
                        value: 0,
                        className: "w-full sm:w-auto",
                        icon: <X className="h-4 w-4" />,
                    },
                    {
                        label: "Yes",
                        variant: "action",
                        value: 1,
                        className: "w-full sm:w-auto",
                        icon: <Check className="h-4 w-4" />,
                    },
                ]}
                onAction={(actionValue) => {
                    if (actionValue === 1) {
                        setHasChanges(false);
                        setShowExitAlert(false);
                        onOpenChange(false);
                    } else {
                        setShowExitAlert(false);
                    }
                }}
            />
        </TooltipProvider >
    );
};
export default ConnectionFieldsDialog;